import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { crrFlashQuestions, type CRRFlashQuestion } from '@/data/crr-flash-questions';

export interface FlashCard extends CRRFlashQuestion {
  acronym?: string;
  explanation_fr?: string;
  explanation_en?: string;
  use_cases_fr?: string[];
  use_cases_en?: string[];
  tags?: string[];
}

async function fetchFlashcards(): Promise<FlashCard[]> {
  const { data, error } = await supabase
    .from('flashcards')
    .select('*')
    .eq('active', true)
    .order('created_at', { ascending: true });

  if (error || !data || data.length === 0) {
    // Fallback to local data
    return crrFlashQuestions.map((q) => ({
      ...q,
      explanation_fr: q.context_fr,
      explanation_en: q.context_en,
      use_cases_fr: [],
      use_cases_en: [],
      tags: [],
    }));
  }

  return data.map((row) => ({
    id: row.id,
    acronym: row.acronym ?? undefined,
    question_fr: row.question_fr,
    question_en: row.question_en,
    answer_fr: row.answer_fr,
    answer_en: row.answer_en,
    context_fr: row.explanation_fr ?? '',
    context_en: row.explanation_en ?? '',
    explanation_fr: row.explanation_fr ?? undefined,
    explanation_en: row.explanation_en ?? undefined,
    use_cases_fr: row.use_cases_fr ?? [],
    use_cases_en: row.use_cases_en ?? [],
    simple_fr: undefined,
    category: (row.category as FlashCard['category']) || 'general',
    tags: row.tags ?? [],
  }));
}

export function useFlashDeck() {
  return useQuery<FlashCard[]>({
    queryKey: ['flashcards'],
    queryFn: fetchFlashcards,
    staleTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    // Defer fetch to avoid extending the critical request chain on initial load
    enabled: typeof window !== 'undefined',
    initialData: undefined,
    placeholderData: [],
  });
}
